import React, { useState, useEffect } from "react";
import { downloadFile, getMetadata } from "../../../services/api/files";
import { useFilesContext } from "../../../context/domain/files/FilesContext";

/**
 * Component for displaying a file.
 *
 * @component
 */
const File = ({
  name,
  icon,
  onClick,
  isGridView,
  comments,
  path,
  entryId,
  abortSignal,
}) => {
  const { enqueueRequest } = useFilesContext(); // Access the enqueue function
  const [thumbnail, setThumbnail] = useState(null);
  const [metadata, setMetadata] = useState(null);

  useEffect(() => {
    if (!path) return;
    if (thumbnail) return; // Avoid fetching if thumbnail is already set

    async function fetchFileData() {
      try {
        const response = await enqueueRequest(() => downloadFile(path, { signal: abortSignal }), "low");
        const { base64_content, content_type } = response;
        const objectUrl = `data:${content_type};base64,${base64_content}`;
        setThumbnail(objectUrl);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching file data:", error);
        }
      }
    }

    fetchFileData();
  }, [path, thumbnail, abortSignal, enqueueRequest]);

  useEffect(() => {
    if (!entryId) return;
    if (!comments) return; // Avoid fetching if comments are not needed
    if (metadata) return; // Avoid fetching if metadata is already set

    async function fetchMetadata() {
      try {
        const data = await enqueueRequest(() => getMetadata(entryId, { signal: abortSignal }), "low");
        setMetadata(data);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching metadata:", error);
        }
      }
    }

    fetchMetadata();
  }, [entryId, metadata, comments, abortSignal, enqueueRequest]);

  return isGridView ? (
    <div
      className="bg-white border rounded-lg p-4 shadow-sm hover:bg-gray-100 hover:cursor-pointer flex flex-col justify-center"
      onClick={onClick}
    >
      <div className="flex items-center justify-center mb-4 text-4xl">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={name}
            className="object-cover w-full h-full rounded-lg"
          />
        ) : (
          <div className="w-full h-full rounded-lg">
            <div className="w-full h-full flex items-center justify-center">
              {icon}
            </div>
          </div>
        )}
      </div>
      <div className="text-center text-sm font-semibold truncate">
        {name || "-"}
      </div>
    </div>
  ) : (
    <div
      className={`grid ${comments ? "grid-cols-3" : "grid-cols-1"} gap-4 p-1 border-b hover:bg-gray-200 hover:cursor-pointer items-center`}
      onClick={onClick}
    >
      <div
        className={`${comments ? "col-span-2" : "col-span-1"} flex items-center space-x-2`}
      >
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={name}
            className="object-cover w-10 h-10 rounded-lg"
          />
        ) : (
          <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
            <div className="w-full h-full flex items-center justify-center">
              {icon}
            </div>
          </div>
        )}
        <span className="text-emerald-950 font-semibold truncate">
          {name || "-"}
        </span>
      </div>
      {comments && (
        <div className="text-gray-600 text-sm truncate col-span-1">
          {metadata?.["results"]?.[0]?.["rscm custom tags"]?.["description"] ||
            "-"}
        </div>
      )}
    </div>
  );
};

export default File;
